import {
	ATHENA_BILLING_PROVIDER_CONFIG_INVALID,
	AthenaBillingProviderError,
} from "../errors.ts";
import type {
	BillingOperationCapability,
	BillingOperationCapabilityReason,
} from "./capabilities.ts";

export type BillingAuthoritySource = "derived" | "declared" | "provider";

export type BillingAuthorityScope =
	| { kind: "organization" }
	| { kind: "profile"; profileId?: string };

export interface BillingCredentialAuthority {
	readonly source: BillingAuthoritySource;
	readonly modes: {
		readonly test: boolean;
		readonly live: boolean;
	};
	readonly scope: BillingAuthorityScope;
	readonly permissions: Readonly<Record<string, boolean>>;
}

/**
 * Absent authority is unenforced provider-native scope (Stripe, catalog).
 * A present but incomplete `authority` object is a config error — never
 * fail-open by treating it as fully granted.
 */
export function readBillingCredentialAuthority(
	providerConfig: Readonly<Record<string, unknown>> | undefined,
): BillingCredentialAuthority | undefined {
	const raw = providerConfig?.authority;
	if (raw == null) {
		return undefined;
	}
	if (typeof raw !== "object" || Array.isArray(raw)) {
		throw new AthenaBillingProviderError({
			code: ATHENA_BILLING_PROVIDER_CONFIG_INVALID,
			message: "billing.providers authority is invalid.",
		});
	}
	const record = raw as Partial<BillingCredentialAuthority>;
	if (record.source == null || record.modes == null || record.scope == null) {
		throw new AthenaBillingProviderError({
			code: ATHENA_BILLING_PROVIDER_CONFIG_INVALID,
			message: "billing.providers authority is incomplete.",
		});
	}
	return {
		modes: {
			live: record.modes.live === true,
			test: record.modes.test === true,
		},
		permissions: record.permissions ?? {},
		scope: record.scope,
		source: record.source,
	};
}

export function grantedPermissionsFromAuthority(
	authority: BillingCredentialAuthority,
): ReadonlySet<string> {
	const granted = new Set<string>();
	for (const [permission, allowed] of Object.entries(authority.permissions)) {
		if (allowed) {
			granted.add(permission);
		}
	}
	return granted;
}

export function authorityAllowsMode(
	authority: BillingCredentialAuthority,
	environment: "test" | "live",
): boolean {
	return authority.modes[environment] === true;
}

export function authorityAllowsProfile(
	authority: BillingCredentialAuthority,
	requestedProfileId?: string | null,
): boolean {
	if (authority.scope.kind === "organization") {
		return true;
	}
	const restricted = authority.scope.profileId;
	const requested =
		requestedProfileId != null && requestedProfileId.length > 0
			? requestedProfileId
			: undefined;
	if (restricted == null || restricted.length === 0) {
		return requested == null;
	}
	if (requested == null) {
		return true;
	}
	return requested === restricted;
}

export function evaluateBillingOperationCapability(input: {
	anyCredentialConfigured: boolean;
	grantedPermissions: ReadonlySet<string>;
	implemented: boolean;
	requiredPermissions: readonly string[];
	scopeAllowed: boolean;
	selectedModeAllowed: boolean;
	unimplementedReason?: BillingOperationCapabilityReason;
}): BillingOperationCapability {
	if (!input.implemented) {
		return unavailable(input.unimplementedReason ?? "unsupported_operation");
	}
	if (!input.anyCredentialConfigured) {
		return unavailable("missing_credential");
	}
	if (!input.selectedModeAllowed) {
		return unavailable("missing_provider_scope");
	}
	if (!input.scopeAllowed) {
		return unavailable("missing_provider_scope");
	}
	for (const permission of input.requiredPermissions) {
		if (!input.grantedPermissions.has(permission)) {
			return unavailable("missing_permission");
		}
	}
	return { available: true };
}

function unavailable(
	reason: BillingOperationCapabilityReason,
): BillingOperationCapability {
	return { available: false, reason };
}
